/**
 * TaskCommentItem — comentário individual com:
 * - kebab menu (Editar / Excluir / Copiar link / Responder)
 * - badge Privado
 * - anexos (chips clicáveis + excluir)
 * - form de resposta inline
 * - linkify no conteúdo HTML
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Copy,
  Download,
  Lock,
  MoreVertical,
  Paperclip,
  Reply,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { apiClient, type TaskComment, type TaskCommentAttachment } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';
import { cn } from '@/lib/utils';
import { linkifyHtml } from '@/lib/linkify';
import { MentionEditor, type MentionMember } from './mention-editor';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeDate(iso: string): string {
  try {
    return formatDistanceToNow(parseISO(iso), { locale: ptBR, addSuffix: true });
  } catch {
    return iso;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '🖼';
  if (mimeType.includes('pdf')) return '📄';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊';
  return '📎';
}

// ---------------------------------------------------------------------------
// Kebab Menu
// ---------------------------------------------------------------------------

interface KebabMenuProps {
  isAuthor: boolean;
  isRoot: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onCopyLink: () => void;
  onReply: () => void;
}

function KebabMenu({ isAuthor, isRoot, onEdit, onDelete, onCopyLink, onReply }: KebabMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded p-1 text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)] transition"
        title="Opções"
      >
        <MoreVertical className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-6 z-50 w-44 rounded-md border border-[var(--border)] bg-[var(--card-bg)] shadow-lg overflow-hidden">
          {isRoot && (
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
              onClick={() => { setOpen(false); onReply(); }}
            >
              <Reply className="h-3.5 w-3.5" />
              Responder
            </button>
          )}
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
            onClick={() => { setOpen(false); onCopyLink(); }}
          >
            <Copy className="h-3.5 w-3.5" />
            Copiar link
          </button>
          {isAuthor && (
            <>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
                onClick={() => { setOpen(false); onEdit(); }}
              >
                <Paperclip className="h-3.5 w-3.5" />
                Editar
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition"
                onClick={() => { setOpen(false); onDelete(); }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Excluir
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attachment chip
// ---------------------------------------------------------------------------

interface AttachmentChipProps {
  taskId: string;
  commentId: string;
  attachment: TaskCommentAttachment;
  isAuthor: boolean;
}

function AttachmentChip({ taskId, commentId, attachment, isAuthor }: AttachmentChipProps) {
  const queryClient = useQueryClient();
  const [downloading, setDownloading] = useState(false);

  const deleteMut = useMutation({
    mutationFn: () => apiClient.tasksDeleteCommentAttachment(taskId, commentId, attachment.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId, 'comments'] });
    },
  });

  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    try {
      const API_URL = (import.meta as { env: Record<string, string> }).env.VITE_API_URL ?? '';
      const url = `${API_URL}/api/tasks/${encodeURIComponent(taskId)}/comments/${encodeURIComponent(commentId)}/attachments/${encodeURIComponent(attachment.id)}/download`;
      const res = await fetch(url, { method: 'GET', credentials: 'include' });
      if (!res.ok) throw new Error('Falha no download');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = attachment.fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch {
      // silencioso
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="flex items-center gap-1.5 rounded border border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] px-2 py-1 text-xs text-[var(--fg)] max-w-full">
      <span className="shrink-0">{fileIcon(attachment.mimeType)}</span>
      <button
        type="button"
        onClick={handleDownload}
        className="flex items-center gap-1 hover:underline truncate"
        title="Baixar"
        disabled={downloading}
      >
        <span className="truncate max-w-[120px]">{attachment.fileName}</span>
        <span className="text-[var(--fg-muted)] shrink-0">({formatBytes(attachment.sizeBytes)})</span>
        <Download className="h-3 w-3 shrink-0 text-[var(--fg-muted)]" />
      </button>
      {isAuthor && (
        <button
          type="button"
          onClick={() => {
            if (window.confirm('Excluir este anexo?')) deleteMut.mutate();
          }}
          disabled={deleteMut.isPending}
          className="ml-auto shrink-0 text-[var(--fg-muted)] hover:text-red-600 transition disabled:opacity-40"
          title="Excluir anexo"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reply form inline
// ---------------------------------------------------------------------------

interface ReplyFormProps {
  taskId: string;
  parentCommentId: string;
  members: MentionMember[];
  onClose: () => void;
}

function ReplyForm({ taskId, parentCommentId, members, onClose }: ReplyFormProps) {
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');

  const addMut = useMutation({
    mutationFn: () =>
      apiClient.tasksAddComment(taskId, { content, parentCommentId }),
    onSuccess: () => {
      setContent('');
      queryClient.invalidateQueries({ queryKey: ['task', taskId, 'comments'] });
      onClose();
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!content.replace(/<[^>]*>/g, '').trim() || addMut.isPending) return;
    addMut.mutate();
  }

  return (
    <form onSubmit={handleSubmit} className="mt-2 flex flex-col gap-2">
      <MentionEditor
        value={content}
        onChange={setContent}
        members={members}
        placeholder="Escreva uma resposta…"
        rows={2}
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={addMut.isPending}
          className="rounded-md bg-[var(--accent)] px-2.5 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-40 transition"
        >
          {addMut.isPending ? 'Enviando…' : 'Enviar resposta'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-[var(--border)] px-2.5 py-1 text-xs text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] transition"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export interface TaskCommentItemProps {
  comment: TaskComment;
  /** Replies (children) deste comentário raiz */
  replies?: TaskComment[];
  taskId: string;
  members: MentionMember[];
  isRoot?: boolean;
  onToast?: (msg: string) => void;
}

export function TaskCommentItem({
  comment,
  replies = [],
  taskId,
  members,
  isRoot = true,
  onToast,
}: TaskCommentItemProps) {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);

  const isAuthor = currentUser?.id != null && comment.userId === currentUser.id;

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);

  const updateMut = useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) =>
      apiClient.tasksUpdateComment(taskId, commentId, { content }),
    onSuccess: () => {
      setEditingId(null);
      setEditContent('');
      queryClient.invalidateQueries({ queryKey: ['task', taskId, 'comments'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (commentId: string) => apiClient.tasksDeleteComment(taskId, commentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['task', taskId, 'comments'] }),
  });

  function handleCopyLink() {
    const url = `${window.location.href.split('#')[0]}#comment-${comment.id}`;
    try { navigator.clipboard.writeText(url); } catch { /* ignora */ }
    onToast?.('Link copiado');
  }

  function handleDelete() {
    if (!window.confirm('Excluir este comentário?')) return;
    deleteMut.mutate(comment.id);
  }

  const isEditing = editingId === comment.id;
  const htmlContent = linkifyHtml(comment.content);

  return (
    <div id={`comment-${comment.id}`} className={cn('flex gap-3', !isRoot && 'ml-8')}>
      {/* Linha conectora para replies */}
      {!isRoot && (
        <div className="absolute -left-5 top-0 h-full w-px bg-[var(--border)]" />
      )}

      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_oklch,var(--accent)_20%,transparent)] text-[var(--accent)]">
        <User className="h-4 w-4" />
      </div>

      <div className="flex-1 min-w-0">
        {/* Header do comentário */}
        <div className="flex items-baseline justify-between gap-1.5">
          <div className="flex flex-wrap items-baseline gap-1.5">
            <span className="text-xs font-semibold text-[var(--fg)]">
              {comment.userName ?? 'Usuário removido'}
            </span>
            <span className="text-[11px] text-[var(--fg-muted)]">
              {relativeDate(comment.createdAt)}
            </span>
            {comment.editedAt && (
              <span className="text-[11px] italic text-[var(--fg-muted)]">editado</span>
            )}
            {comment.isPrivate && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
                <Lock className="h-2.5 w-2.5" />
                Privado
              </span>
            )}
          </div>
          <KebabMenu
            isAuthor={isAuthor}
            isRoot={isRoot}
            onEdit={() => {
              setEditingId(comment.id);
              setEditContent(comment.content);
            }}
            onDelete={handleDelete}
            onCopyLink={handleCopyLink}
            onReply={() => setShowReplyForm(true)}
          />
        </div>

        {/* Conteúdo */}
        {isEditing ? (
          <div className="mt-1.5 flex flex-col gap-2">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={3}
              autoFocus
              className="w-full resize-none rounded-md border border-[var(--border)] bg-[var(--input-bg,var(--card-bg))] px-3 py-2 text-sm text-[var(--fg)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            />
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!editContent.trim() || updateMut.isPending}
                onClick={() => updateMut.mutate({ commentId: comment.id, content: editContent })}
                className="rounded-md bg-[var(--accent)] px-2.5 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-40 transition"
              >
                {updateMut.isPending ? 'Salvando…' : 'Salvar'}
              </button>
              <button
                type="button"
                onClick={() => { setEditingId(null); setEditContent(''); }}
                className="rounded-md border border-[var(--border)] px-2.5 py-1 text-xs text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <div
            className="mt-1 whitespace-pre-wrap text-sm text-[var(--fg)] leading-relaxed [&_.mention-chip]:inline-block [&_.mention-chip]:rounded [&_.mention-chip]:bg-yellow-100 [&_.mention-chip]:text-yellow-800 [&_.mention-chip]:px-1 [&_.mention-chip]:font-medium [&_a]:text-[var(--accent)] [&_a]:underline"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        )}

        {/* Anexos */}
        {comment.attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {comment.attachments.map((att) => (
              <AttachmentChip
                key={att.id}
                taskId={taskId}
                commentId={comment.id}
                attachment={att}
                isAuthor={isAuthor}
              />
            ))}
          </div>
        )}

        {/* Form de resposta */}
        {showReplyForm && (
          <ReplyForm
            taskId={taskId}
            parentCommentId={comment.id}
            members={members}
            onClose={() => setShowReplyForm(false)}
          />
        )}

        {/* Replies (1 nível) */}
        {isRoot && replies.length > 0 && (
          <div className="relative mt-3 flex flex-col gap-3 border-l-2 border-[var(--border)] pl-4">
            {replies.map((reply) => (
              <TaskCommentItem
                key={reply.id}
                comment={reply}
                taskId={taskId}
                members={members}
                isRoot={false}
                onToast={onToast}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
